import { ANIMATABLE, AXES, AXIS_TRACKS, axisTracksPresent, combineAxes, evalTrack, setKey, splitVecTrack } from '../core/anim.js';
import { sourceTime } from '../core/video-time.js';
import { applyLook, applyReveal, syncTransform } from '../scene/objects.js';
import { eraseOf, revealOf } from '../scene/textarea.js';
import { settleVideos, syncVideoTime } from '../scene/video.js';
import { emit, emitNextFrame, requestRender } from '../state/bus.js';
import { S } from '../state/store.js';

/* THE PLAYHEAD — where the scene is in time, and how it gets there.

   core/anim.js knows how to turn a track and a time into a value. This is the
   part that knows what to do with that value: it walks the scene, evaluates
   every track, and pushes the results into the objects.

   ── Into the OBJECTS, not into the three.js nodes ────────────────────────

   The purer design evaluates straight onto the node and leaves the document
   untouched, which sounds obviously right and is not. Too much of the app
   reads the object directly: updateGizmo takes its position from o.pos
   (scene/gizmo.js), the inspector's number fields are bound to o.pos itself
   (ui/rail-right.js's inspectorRefs), and the drag handlers write to it
   (ui/interaction.js). Evaluating past all of that would leave the gizmo
   floating where the object used to be, the inspector showing numbers that
   are no longer true, and every one of those paths needing to learn about
   animation.

   Writing into the object instead means gizmo, selection box, inspector
   values and face arrow all follow the playhead with no changes at all.

   The price, stated plainly: a project saved while the playhead sits at two
   seconds stores that frame's pose in o.pos. It is harmless because the
   TRACK is the source of truth and wins on the next evaluation — but only
   because applyAnimation() runs after a project loads. That call is not
   optional; without it a reopened project shows its last-scrubbed pose until
   something happens to move the playhead. */

/* Which sync a property needs once its value has been written. Transform
   properties go to the node's matrix; the rest are material or light state.
   Split so a scene animating only opacity never touches a single matrix. */
const NEEDS = {
  pos: 'xform', rot: 'xform', scale: 'xform', visible: 'xform',
  opacity: 'look', color: 'look', intensity: 'look',
  /* A third kind: the value lives in a BITMAP, not in a matrix or a material,
     so neither of the other two syncs can carry it. */
  reveal: 'paint', erase: 'paint'
};
/* An axis of a vector is a transform like the vector it belongs to. */
for (const [base, tracks] of Object.entries(AXIS_TRACKS)) for (const p of tracks) NEEDS[p] = NEEDS[base];

/* Vectors are assigned COMPONENT-WISE rather than replaced. o.pos is not just
   a value here — ui/controls.js's vec3Row keeps a live reference to that
   exact object so it can write into it during a scrub, and the inspector's
   refresh reads back through the same reference. Swapping in a fresh {x,y,z}
   would leave every one of those bound to an orphan. */
function assign(o, prop, v) {
  if (prop === 'intensity') { o.params.intensity = v; return; }
  /* Both of these live in params rather than on the object, so neither can
     use the component-wise path below. */
  if (prop === 'reveal') { o.params.reveal = v; return; }
  if (prop === 'erase') { o.params.erase = v; return; }
  /* One axis of a separated vector ('pos.x'): written into the same object the
     vector's own row is bound to, for the same reason as the whole-vector case
     just below. */
  if (prop.includes('.')) { const [base, axis] = prop.split('.'); o[base][axis] = v; return; }
  if (v && typeof v === 'object') {
    const t = o[prop];
    t.x = v.x; t.y = v.y; t.z = v.z;
    return;
  }
  o[prop] = v;
}

/**
 * Put the whole scene at time `t`.
 *
 * Safe to call when nothing is animated — it walks the object list and does
 * nothing, which is the common case and costs a loop over a handful of items.
 *
 * @returns {boolean} whether anything actually moved, so a caller can skip a
 *                    render it does not need.
 */
export function applyAnimation(t = S.playhead) {
  let touched = false;
  for (const o of S.objects) {
    /* A clip's frame is a function of the playhead exactly as a keyframed
       value is, so it is resolved here and NOWHERE else — see the header of
       core/video-time.js for why one call site is the whole guarantee that
       the preview and the export cannot disagree about which frame belongs
       at time t. Fire-and-forget: the preview catches up a frame later, and
       an export awaits settleAt() below instead. */
    if (o.type === 'video') { syncVideoTime(o, sourceTime(o.params, t)); touched = true; }
    if (!o.anim) continue;
    let xform = false, look = false, paint = false;
    for (const prop in ANIMATABLE) {
      const keys = o.anim[prop];
      if (!keys) continue;
      const v = evalTrack(keys, t);
      /* undefined means the track had nothing in it — leave the property
         alone rather than assigning undefined over a real value. */
      if (v === undefined) continue;
      assign(o, prop, v);
      const need = NEEDS[prop];
      if (need === 'xform') xform = true;
      else if (need === 'paint') paint = true;
      else look = true;
    }
    if (xform) syncTransform(o);
    if (look) applyLook(o);
    if (paint) applyReveal(o);
    touched = touched || xform || look || paint;
  }
  if (touched) requestRender();
  return touched;
}

/**
 * Put the scene at time `t` AND wait until it can actually be rendered.
 *
 * The async twin of applyAnimation, and the one every exporter calls. It
 * exists because export/frame.js's renderFrame is synchronous — deliberately,
 * since it is about renderer configuration and pixel geometry, not about
 * time — while a video frame arrives from a decoder that cannot be. Putting
 * the await here rather than inside renderFrame keeps that boundary intact
 * and means no exporter has to know which object types need decoding.
 *
 * Returns immediately for a scene with no video in it.
 */
export async function settleAt(t) {
  applyAnimation(t);
  await settleVideos(t);
}

/* Time is FRAMES, not a continuum.

   The output is a video: whatever happens between frame 44 and frame 45 is
   not in it. Letting the playhead rest at 1.4733s shows a picture the export
   will never produce, and letting a keyframe sit there means its timing is
   quietly rounded at export to somewhere the person never chose. Everything
   here lands on a frame boundary instead, so what is on screen while
   authoring is a frame that will actually exist. */
export const frameDur = () => 1 / Math.max(1, S.timeline.fps);
export const toFrame = t => Math.round(t / frameDur());
export const quantise = t => toFrame(t) * frameDur();
/** Total frames in the timeline — the last one is duration minus a frame. */
export const frameCount = () => Math.max(1, Math.round(S.timeline.duration * S.timeline.fps));

/** Whether anything in the scene is keyframed — the test for "is there a
 *  video to export at all", used by the Export screen and by the Flat/Split
 *  watermark preview. One definition, because two that drift would have the
 *  preview and the export card disagreeing about the same project. */
export const hasMotion = () => S.objects.some(o => o.anim && Object.keys(o.anim).length);

/**
 * Put every keyframe on a whole frame of the current rate.
 *
 * Keys made before keying snapped to the grid — or under a different fps —
 * sit between frames: each tooltip rounds to the nearest frame and says the
 * same number, while the lanes draw them visibly apart. Moving each by under
 * half a frame makes what is drawn and what is said agree. Two keys of one
 * track that land on the same frame keep the later one, as setKey always does.
 * @returns {number} how many keys moved
 */
export function snapKeysToFrames() {
  let moved = 0;
  for (const o of S.objects) {
    if (!o.anim) continue;
    for (const prop of Object.keys(o.anim)) {
      let keys = null;
      for (const k of o.anim[prop]) {
        const t = quantise(k.t);
        if (Math.abs(t - k.t) > 1e-9) moved++;
        keys = setKey(keys, t, k.v, k.ease);
      }
      o.anim[prop] = keys;
    }
  }
  return moved;
}

/** Move the playhead and put the scene there. Clamped to the timeline, so
 *  nothing can scrub into negative time or past the end, and quantised to a
 *  whole frame. */
export function setPlayhead(t) {
  const clamped = quantise(Math.min(S.timeline.duration, Math.max(0, +t || 0)));
  if (clamped === S.playhead) return;
  S.playhead = clamped;
  applyAnimation(clamped);
  /* Two events, because they have different audiences: the timeline's own
     ruler needs every scrub, while the inspector only wants to refresh the
     numbers it is showing. Coalesced to one per frame — see bus.js — so
     dragging across the ruler cannot outrun the DOM. */
  emit('playhead:moved', clamped);
  emit('anim:applied', clamped);
}

/* ── editing an animated property ──────────────────────────────────────────

   When a property has a track, the value on the object is no longer the
   authored value — it is whatever the playhead last evaluated to, and the
   next evaluation will overwrite whatever an edit puts there. So an edit has
   to land on a KEYFRAME at the playhead instead.

   Called on every pointermove of a drag rather than once on release. That
   sounds wasteful and is the thing that makes it work: the drag writes the
   key, the next evaluation reads that same key back, and the two agree by
   construction. Capturing only on release would mean the object snapping back
   to its keyframed pose on every intermediate frame — an unusable gizmo — and
   would need a "is a drag in progress" flag threaded through the evaluator to
   avoid it. Writing a key is a sort of a handful of numbers; it does not
   register against a pointer-rate budget. */

/* The twin of assign() above: two animatable properties live in `params`
   rather than on the object, so neither reading nor writing one can go
   through the plain path. Exported because the timeline dock writes keys of
   its own and had its own copy of the intensity special case — which is how
   the two quietly stop agreeing the moment a third such property appears. */
export const readProp = (o, prop) =>
  prop.includes('.') ? o[prop.split('.')[0]][prop.split('.')[1]]
  : prop === 'intensity' ? o.params.intensity
  : prop === 'reveal' ? revealOf(o)
  : prop === 'erase' ? eraseOf(o)
  : o[prop];

/** Record the current value of `prop` as a keyframe at the playhead — but
 *  only if that property is animated. Untracked properties are left entirely
 *  alone, which is what keeps this safe to call from every edit path in the
 *  app: on a scene with no animation it does nothing at all.
 *  @returns {boolean} whether a keyframe was written. */
export function captureKey(o, prop) {
  if (!o || !o.anim) return false;
  /* A separated vector: key the axes that CHANGED, and only those. Its point is
     that a move along X must not stamp a key into Y and Z — which would freeze
     them at whatever they were interpolating to, the very thing separating them
     is for. An axis has changed if its value is no longer what its own track
     says it should be at the playhead; an untouched one is exactly that (both
     came out of the same evaluation), so this needs nobody to say which axis
     was edited — a gizmo drag, an arrow-key nudge and a typed number all arrive
     here the same way. */
  const axes = axisTracksPresent(o, prop);
  let wrote = false;
  for (const p of axes)
    if (Math.abs(readProp(o, p) - evalTrack(o.anim[p], S.playhead)) > 1e-9) wrote = writeKey(o, p) || wrote;
  if (axes.length && !o.anim[prop]) return wrote;
  if (!o.anim[prop]) return false;
  return writeKey(o, prop) || wrote;
}

function writeKey(o, prop) {
  const before = o.anim[prop].length;
  o.anim[prop] = setKey(o.anim[prop], quantise(S.playhead), readProp(o, prop));

  /* Tell the timeline a diamond appeared. Without this the keyframe existed
     the instant it was written but stayed invisible until something else
     happened to rebuild the dock — selecting an object, resizing the window —
     so a drag would produce a keyframe "later, sometimes", which is the worst
     possible feedback for an auto-keyframing tool.

     Only when the SET of keyframes changed, not on every write. A drag writes
     on every pointermove, but after the first one setKey is REPLACING the key
     at that same instant: the value moves, the diamond does not, and there is
     nothing to redraw. Coalesced to one per frame besides, so even a genuine
     burst cannot rebuild the dock at pointer rate. */
  if (o.anim[prop].length !== before) emitNextFrame('anim:changed', o);
  return true;
}

/* THERE IS NO "capture the transform". There was, and it wrote a keyframe on
   every animated one of pos, rot and scale whatever the edit had actually
   changed — on the reasoning that a gizmo drag should not have to know which
   property it was dragging. It always did know: gz.mode is exactly move,
   rotate or scale, every numeric row carries its own prop, and a nudge moves
   nothing but position.

   What it cost is that the three could not be animated separately. Keyframe
   scale and rotation, then move the object, and the move stamped a key into
   the scale and rotation tracks at the playhead — freezing whatever they
   happened to be interpolating to at that instant. Reported as the three
   being "somehow linked together".

   So each caller names the property it changed, and captureKey above writes
   that one. */

/** Start animating a property: creates the track with a keyframe at the
 *  playhead holding whatever the value is right now. The stopwatch. */
export function addTrack(o, prop) {
  if (!ANIMATABLE[prop] || (o.anim && o.anim[prop])) return;
  /* A vector is combined or separated, never both: starting the other form
     while one exists would have the two fight over the same value every frame.
     splitProp / combineProp are how to change form. */
  if (ANIMATABLE[prop].of ? o.anim?.[ANIMATABLE[prop].of] : axisTracksPresent(o, prop).length) return;
  o.anim = o.anim || {};
  o.anim[prop] = setKey(null, quantise(S.playhead), readProp(o, prop));
  /* Re-evaluate rather than assume nothing moved. It usually is a no-op — the
     first keyframe holds the value that is already on screen — but setPlayhead
     short-circuits when the time has not changed, so a track added or removed
     WITHOUT the playhead moving would otherwise not reach the scene until
     something else happened to nudge it. That gap is invisible in the common
     case and confusing in exactly the case that matters: turning a property's
     stopwatch off should put the scene back on screen immediately. */
  applyAnimation();
  emit('anim:changed', o);
}

/** Separate a vector's track into X, Y and Z tracks, key for key. Nothing moves:
 *  the three together say exactly what the one did. */
export function splitProp(o, base) {
  if (!o.anim?.[base] || !AXIS_TRACKS[base]) return;
  const t = splitVecTrack(o.anim[base]);
  delete o.anim[base];
  for (const a of AXES) o.anim[base + '.' + a] = t[a];
  applyAnimation();
  emit('anim:changed', o);
}

/** Bring the axis tracks back into one vector track (mergeAxisTracks says what
 *  that keeps and what it can lose). An axis with no track holds its value. */
export function combineProp(o, base) {
  if (!combineAxes(o, base)) return;
  applyAnimation();
  emit('anim:changed', o);
}

/** Stop animating it. The value stays wherever the playhead last put it —
 *  the same thing After Effects does, and the only choice that does not
 *  surprise: silently jumping back to some earlier "original" the user has
 *  not seen since they started keyframing would look like a bug. */
export function removeTrack(o, prop) {
  if (!o.anim || !o.anim[prop]) return;
  delete o.anim[prop];
  if (!Object.keys(o.anim).length) delete o.anim;
  applyAnimation();
  emit('anim:changed', o);
}

/* ── playback ──────────────────────────────────────────────────────────────
   Advanced by REAL elapsed time, not by a fixed step per frame. If the
   browser drops a frame — which it will, on a scene heavy enough to be worth
   animating — a fixed step would quietly play the preview back in slow
   motion, and the person watching would tune their timing against a lie. */

let raf = 0, last = 0;

function tick(now) {
  if (!S.playing) return;
  const dt = (now - last) / 1000;
  last = now;
  let t = S.playhead + dt;
  /* Looping is the default because every screen this app targets plays its
     content on a loop, so it is the honest preview — including the seam where
     the last frame meets the first, which is the single most likely thing to
     be wrong and the hardest to notice any other way.

     Turned off, playback STOPS on the final frame rather than freezing a
     hair past it: the last frame is a frame that will exist in the export,
     and leaving the playhead at exactly `duration` would sit one frame beyond
     the end, showing something the file never contains. */
  if (t >= S.timeline.duration) {
    if (S.timeline.loop === false) {
      S.playhead = Math.max(0, S.timeline.duration - frameDur());
      applyAnimation(S.playhead);
      requestRender();
      emit('playhead:moved', S.playhead);
      pause();
      return;
    }
    t = t % S.timeline.duration;
  }
  S.playhead = t;
  applyAnimation(t);
  requestRender();
  emit('playhead:moved', t);
  raf = requestAnimationFrame(tick);
}

export function play() {
  if (S.playing) return;
  S.playing = true;
  last = performance.now();
  raf = requestAnimationFrame(tick);
  emit('playing:changed', true);
}

export function pause() {
  if (!S.playing) return;
  S.playing = false;
  cancelAnimationFrame(raf);
  raf = 0;
  /* ONTO A FRAME. Playback advances by real elapsed time, so it stops wherever
     the clock happened to be — between two frames, showing the nearest one's
     number. A key written from there landed off the grid, a fraction of a frame
     from the key written next to it after the playhead had been placed by
     hand: the two read as the same frame in every tooltip and sat visibly
     apart in the lanes. */
  S.playhead = quantise(S.playhead);
  applyAnimation(S.playhead);
  requestRender();
  emit('playhead:moved', S.playhead);
  /* The inspector is not refreshed during playback — sixty DOM writes a
     second to show numbers nobody can read would be pure cost — so it is
     refreshed once here, on the frame playback stopped at. */
  emit('anim:applied', S.playhead);
  emit('playing:changed', false);
}

export const togglePlay = () => (S.playing ? pause() : play());
